"use client";

import React from "react";
import { Facebook, Instagram, Linkedin, Twitter, LoaderCircle } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
function FooterMenuLinks() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="22831" className="flex flex-col gap-3">
        {[1, 2, 3].map(i => <div key={i} className="h-4 w-24 bg-muted/20 animate-pulse rounded" />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) return null;
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-muted-foreground hover:text-primary transition-colors text-sm font-sans tracking-normal">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-4 bg-accent/10 border border-accent/20 rounded-none">
        <p className="text-accent-foreground text-sm font-sans">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="first_name" render={({
      field
    }) => <FormItem>
            <FormLabel className="sr-only">First Name</FormLabel>
            <FormControl>
              <Input placeholder="Full Name (Optional)" {...field} className="bg-transparent border-0 border-b border-input rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/50 font-sans" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="sr-only">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="Email Address" {...field} className="bg-transparent border-0 border-b border-input rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/50 font-sans" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="agree_terms" rules={{
      required: "You must agree to the terms"
    }} render={({
      field: {
        value,
        onChange,
        ...field
      }
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0">
            <FormControl>
              <input type="checkbox" checked={value} onChange={onChange} className="mt-1 size-4 rounded-none border-input bg-transparent text-primary focus:ring-primary/20 focus:ring-offset-background" {...field} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-[10px] text-muted-foreground font-light leading-tight tracking-normal">
                I consent to receive exclusive updates and event invitations.
              </FormLabel>
              <FormMessage />
            </div>
          </FormItem>} />

      {submitError && <p className="text-destructive text-xs font-sans">{submitError}</p>}

      <Button type="submit" disabled={isSubmitting} className="w-full rounded-none font-sans tracking-widest uppercase text-xs h-10" variant="primary">
        {isSubmitting ? <>
            <LoaderCircle className="mr-2 size-4 animate-spin" />
            Joining...
          </> : "Join the list"}
      </Button>
    </Form>;
}
export default function MainSiteFooter() {
  const currentYear = new Date().getFullYear();
  const businessName = "Echelon Soiree";
  const manifesto = "Echelon Soiree is a boutique studio curating upscale soirées and corporate gatherings for discerning clients. We blend sophisticated design with meticulous coordination to deliver unforgettable experiences.";
  const shopLinks = [{
    label: "Our Experience",
    to: "/ExperiencePage"
  }, {
    label: "Curated Portfolio",
    to: "/CurationsPage"
  }, {
    label: "Inquire Now",
    to: "/InquirePage"
  }, {
    label: "Private Events",
    to: "#"
  }];
  return <footer id="footer" className="bg-background text-foreground pt-20 pb-10 border-t border-border">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-16">
          
          <div className="space-y-6">
            <Link to="/HomePage" className="block">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1582385/img/logo.png" alt={businessName} className="h-6 w-auto object-contain grayscale" />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-xs font-sans font-light">
              {manifesto}
            </p>
          </div>

          <div className="space-y-6">
            <h3 className="text-xs font-medium tracking-widest uppercase text-primary font-sans">The Studio</h3>
            <ul className="space-y-3">
              {shopLinks.map((link, i) => <li key={i} data-index={i}>
                  <Link to={link.to} className="text-muted-foreground hover:text-primary transition-colors text-sm font-sans font-light">
                    {link.label}
                  </Link>
                </li>)}
            </ul>
          </div>

          <div className="space-y-6">
            <h3 className="text-xs font-medium tracking-widest uppercase text-primary font-sans">Services</h3>
            <MenuProvider menu_id="46" className="w-full">
              <FooterMenuLinks />
            </MenuProvider>
          </div>

          <div className="space-y-6">
            <h3 className="text-xs font-medium tracking-widest uppercase text-primary font-sans">Newsletter</h3>
            <p className="text-muted-foreground text-sm font-sans font-light">
              Subscribe to receive updates, access to exclusive deals, and curated insights.
            </p>
            
            <FormProvider formId="newsletter_signup_home" sectionName="footer-newsletter" defaultValues={{
            first_name: "",
            email: "",
            agree_terms: false
          }} validationRules={{
            email: {
              required: true,
              pattern: "email"
            },
            agree_terms: {
              required: true
            }
          }} submitText="Join" successMessage="Welcome to the inner circle.">
              <NewsletterForm />
            </FormProvider>
          </div>
        </div>

        <Separator className="bg-border/40 mb-8" />

        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-6">
            <Link to="https://facebook.com" newTab aria-label="Facebook" className="text-muted-foreground hover:text-primary transition-colors">
              <Facebook className="size-4" />
            </Link>
            <Link to="https://instagram.com" newTab aria-label="Instagram" className="text-muted-foreground hover:text-primary transition-colors">
              <Instagram className="size-4" />
            </Link>
            <Link to="https://twitter.com" newTab aria-label="Twitter" className="text-muted-foreground hover:text-primary transition-colors">
              <Twitter className="size-4" />
            </Link>
            <Link to="https://linkedin.com" newTab aria-label="LinkedIn" className="text-muted-foreground hover:text-primary transition-colors">
              <Linkedin className="size-4" />
            </Link>
          </div>

          <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8 text-[10px] text-muted-foreground uppercase tracking-widest font-sans">
            <p>&copy; {currentYear} {businessName}.</p>
            <div className="flex gap-6">
              <Link to="#" className="hover:text-primary transition-colors">Privacy Policy</Link>
              <Link to="#" className="hover:text-primary transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>;
}